import { z } from "zod"

// ── Le support : les limites, le message, le contexte technique et la vue d'une copie (chantier SUP) ──

const MB = 1024 * 1024

export const SUPPORT_LIMITS = {
  /** R2 : caracteres d'un message, blancs autour exclus */
  bodyMax: 5000,
  /** R3 : captures par message */
  shotsMax: 3,
  /** Une capture, apres reduction dans le navigateur */
  shotMaxBytes: 4 * MB,
  /** R7 : la copie du projet, avant compression (comme MAX_IMPORT_BYTES) */
  snapshotMaxBytes: 50 * MB,
  /** R8 : demandes par utilisateur sur 24 heures glissantes */
  ticketsPerDay: 10,
  /** Le contexte technique, en JSON */
  contextMaxBytes: 256 * 1024,
  /** Au-dela, l'avertissement du projet lourd */
  heavySnapshotBytes: 10 * MB,
  /** R15 : jours de conservation de la copie apres la resolution */
  retentionDays: 90,
  /** Une requete entiere (captures, copie, contexte) : la limite du proxy, next.config.ts */
  requestMaxBytes: 64 * MB,
  /** Le sujet d'une demande : les premiers mots du message */
  subjectMax: 120,
} as const

export const supportKindSchema = z.enum(["BUG", "QUESTION", "IDEA"])
export type SupportKind = z.infer<typeof supportKindSchema>

export const supportStatusSchema = z.enum(["NEW", "IN_PROGRESS", "ANSWERED", "RESOLVED"])
export type SupportStatus = z.infer<typeof supportStatusSchema>

/** Le statut d'une demande apres une reponse de l'admin (R13) : Repondu, En cours ou Resolu, jamais Nouveau */
export const adminReplyStatusSchema = z.enum(["ANSWERED", "IN_PROGRESS", "RESOLVED"])

/** Les filtres de la liste de l'administration (P09) : « a traiter » (Nouveau et En cours), un statut, ou toutes */
export const adminFilterSchema = z.enum(["open", "NEW", "IN_PROGRESS", "ANSWERED", "RESOLVED", "all"])
export type AdminFilter = z.infer<typeof adminFilterSchema>

/** Le numero d'une demande dans une adresse, ou null s'il n'en est pas un */
export const parseTicketNumber = (raw: string) => (/^\d{1,9}$/.test(raw) ? Number(raw) : null)

export const EMPTY_BODY_MESSAGE = "Écrivez quelques mots pour que nous puissions vous aider."

/** R2 : le message, coupe aux blancs, de 1 a 5 000 caracteres */
export const supportBodySchema = z
  .string()
  .trim()
  .min(1, EMPTY_BODY_MESSAGE)
  .max(SUPPORT_LIMITS.bodyMax, "Votre message dépasse 5 000 caractères.")

export const supportMessageSchema = z.object({ body: supportBodySchema })

const text = z.string().max(2000)
const at = z.string().max(40)
const viewModeSchema = z.enum(["2d", "3d", "visit", "section"])

/** Le contexte technique d'une demande ; chaque partie est facultative (R6 : leger ou complet) */
export const supportContextSchema = z
  .object({
    page: z.object({
      path: text,
      title: text,
      view: viewModeSchema.optional(),
      floorName: text.optional(),
      tool: text.optional(),
      selection: z.object({ type: text, label: text }).optional(),
    }).optional(),
    project: z.object({
      id: text,
      name: text,
      revision: z.number().int().optional(),
      savedRevision: z.number().int().optional(),
      lastSaveAt: at.optional(),
      lastSaveOk: z.boolean().optional(),
      dirtyCount: z.number().int().optional(),
    }).optional(),
    errors: z.array(z.object({ at, message: text, source: text.optional() })).max(20).optional(),
    requests: z.array(z.object({ at, method: z.string().max(10), path: text, status: z.number().int(), message: text.optional() })).max(20).optional(),
    trail: z.array(z.object({ at, label: text })).max(30).optional(),
    env: z.object({
      userAgent: text,
      platform: text.optional(),
      screen: text.optional(),
      viewport: text.optional(),
      dpr: z.number().optional(),
      language: text.optional(),
      timeZone: text.optional(),
      gpu: text.optional(),
      version: text,
      commit: text.optional(),
      live: z.boolean().optional(),
    }).optional(),
  })
  .refine((c) => new TextEncoder().encode(JSON.stringify(c)).length <= SUPPORT_LIMITS.contextMaxBytes, {
    message: "Le contexte technique est trop lourd.",
  })
export type SupportContext = z.infer<typeof supportContextSchema>

const point = z.tuple([z.number().finite(), z.number().finite(), z.number().finite()])

/** La vue a restituer a l'ouverture d'une copie de support (P11) */
export const supportViewSchema = z.object({
  viewMode: viewModeSchema,
  activeFloorId: z.string().optional(),
  selectedElementId: z.string().optional(),
  selectedElementType: z.string().optional(),
  camera: z.object({ position: point, target: point }).optional(),
})
export type SupportView = z.infer<typeof supportViewSchema>

/** Le sujet d'une demande : les premiers mots du message, 120 caracteres au plus, sans couper un mot */
export function buildSubject(body: string): string {
  const flat = body.replace(/\s+/g, " ").trim()
  const max = SUPPORT_LIMITS.subjectMax
  if (flat.length <= max) return flat
  const head = flat.slice(0, max - 1)
  const cut = head.lastIndexOf(" ")
  return `${cut > 0 ? head.slice(0, cut) : head}…`
}
